import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
} from 'react';

interface NavigationContextType {
  navigateBack: (screen?: string) => void;
  navigateForward: (screen: string) => void;
  navigateBackToStart: () => void;
  currentScreen: string;
  canNavigateBack: boolean;
  disableBackNavigation: () => void;
  enableBackNavigation: () => void;
  direction: 'right' | 'left';
}

interface NavigationProviderProps {
  children: ReactNode;
  initialScreen?: string;
}

const NavigationContext = createContext<NavigationContextType | undefined>(
  undefined,
);

export const NavigationProvider: React.FC<NavigationProviderProps> = ({
  children,
  initialScreen = 'default-initial',
}) => {
  const [screenStack, setScreenStack] = useState<string[]>([initialScreen]);
  const [backNavigationEnabled, setBackNavigationEnabled] = useState(true);
  const [direction, setDirection] = useState<'right' | 'left'>('right');

  const navigateBack = (screen?: string) => {
    if (!backNavigationEnabled) return;

    if (screen) {
      const screenIndex = screenStack.findIndex((s) => s === screen);
      if (screenIndex >= 0) {
        setDirection('left');
        setScreenStack((prevScreenStack) =>
          prevScreenStack.slice(0, screenIndex + 1),
        );
      }
    } else if (screenStack.length > 1) {
      setDirection('left');
      setScreenStack((prevScreenStack) => prevScreenStack.slice(0, -1));
    }
  };

  const navigateForward = (screen: string) => {
    setDirection('right');
    setScreenStack((prevScreenStack) => [...prevScreenStack, screen]);
  };

  const navigateBackToStart = () => {
    setDirection('left');
    setScreenStack([initialScreen]);
  };

  const canNavigateBack = backNavigationEnabled && screenStack.length > 1;

  const disableBackNavigation = () => setBackNavigationEnabled(false);
  const enableBackNavigation = () => setBackNavigationEnabled(true);

  const contextValue = useMemo(
    () => ({
      navigateBack,
      navigateForward,
      navigateBackToStart,
      currentScreen: screenStack[screenStack.length - 1],
      canNavigateBack,
      disableBackNavigation,
      enableBackNavigation,
      direction,
    }),
    [
      screenStack,
      backNavigationEnabled,
      direction,
      canNavigateBack,
      navigateBack,
      navigateForward,
      navigateBackToStart,
    ],
  );

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = (): NavigationContextType => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};
